import { Loader2, Search, Star, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useToast } from "../ui/Toast";
import api from "../../services/api";

const PlaybookLibrary = ({ onEdit, refreshTrigger }) => {
  const toast = useToast();
  const [playbooks, setPlaybooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    fetchPlaybooks();
  }, [refreshTrigger]);

  const fetchPlaybooks = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/playbooks");
      if (data.success) {
        setPlaybooks(data.data);
      }
    } catch (error) {
      console.error("Failed to fetch playbooks", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this playbook?")) return;
    
    try {
        await api.delete(`/playbooks/${id}`);
        setPlaybooks(prev => prev.filter(p => p.id !== id));
    } catch (error) {
        toast.error("Failed to delete.");
    }
  };

  const filtered = playbooks.filter(p => p.title.toLowerCase().includes(filter.toLowerCase()));

  if (loading) return <div className="flex justify-center p-12"><Loader2 className="animate-spin text-indigo-500" /></div>;

  if (playbooks.length === 0) {
      return (
          <div className="text-center py-20 bg-slate-800/50 rounded-2xl border border-slate-700 border-dashed">
              <Star className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white">No Plays Yet</h3>
              <p className="text-slate-400 mt-2">Document your first A+ setup to start building your edge.</p>
          </div>
      );
  }

  return (
    <div className="space-y-6">
        {/* Search */}
        <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
            <input 
                type="text" 
                placeholder="Search definitions..." 
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg pl-10 pr-4 py-2 text-white focus:border-indigo-500 outline-none"
            />
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map(playbook => (
                <div 
                    key={playbook.id}
                    onClick={() => onEdit(playbook)}
                    className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden hover:border-indigo-500/50 transition-all cursor-pointer group shadow-lg"
                >
                    {/* Thumbnail / Header */}
                    <div className="h-32 bg-gradient-to-br from-slate-900 to-slate-800 relative p-4 flex flex-col justify-between">
                         <div className="flex justify-between items-start">
                             <span className={`px-2 py-1 rounded text-xs font-bold ${
                                 playbook.grade?.includes('A') ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/20' : 
                                 playbook.grade?.includes('B') ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/20' :
                                 'bg-slate-700 text-slate-300'
                             }`}>
                                 {playbook.grade || 'Ungraded'}
                             </span>
                             <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                 <button onClick={(e) => handleDelete(playbook.id, e)} className="p-1.5 bg-slate-900/80 rounded-full text-red-400 hover:bg-slate-900 hover:text-red-300">
                                    <Trash2 size={14} />
                                 </button>
                             </div>
                         </div>
                         <h3 className="font-bold text-lg text-white truncate">{playbook.title}</h3>
                    </div>

                    {/* Content */}
                    <div className="p-4 space-y-4">
                        <div>
                            <p className="text-xs text-slate-500 uppercase font-bold mb-1">Context</p>
                            <p className="text-sm text-slate-300 font-medium">{playbook.setupType || "General Concept"}</p>
                        </div>
                        
                        {/* Indicators Chips */}
                        {playbook.indicators?.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                                {playbook.indicators.slice(0,3).map((ind, i) => (
                                    <span key={i} className="text-[10px] bg-slate-700/50 text-slate-400 px-2 py-0.5 rounded-full border border-slate-700">
                                        {ind}
                                    </span>
                                ))}
                                {playbook.indicators.length > 3 && (
                                    <span className="text-[10px] text-slate-500 pl-1">+{playbook.indicators.length - 3}</span>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            ))}
        </div>
    </div>
  );
};

export default PlaybookLibrary;
